import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { getFilms, getFilmRatings } from '../lib/supabase';
import FilmCard from '../components/FilmCard';
import Navbar from '../components/Navbar';
import RequestFilmModal from '../components/RequestFilmModal';
import { Spinner, GoldBtn } from '../components/UI';
import { searchOMDB, omdbToFilm } from '../lib/omdb';

const ALL_LANGS  = 'All Languages';
const ALL_GENRES = 'All Genres';
const ALL_TYPES  = 'All';

export default function HomePage({ onFilmClick, onAdminClick, onAuthOpen, onChatClick, onBattleClick, onRankingsClick, onTasteClick }) {
  const [films, setFilms]         = useState([]);
  const [filmStats, setFilmStats] = useState({});
  const [loading, setLoading]     = useState(true);
  const [showRequest, setShowRequest] = useState(false);
  const [search, setSearch]           = useState('');
  const [filterType, setFilterType]   = useState(ALL_TYPES);
  const [filterLang, setFilterLang]   = useState(ALL_LANGS);
  const [filterGenre, setFilterGenre] = useState(ALL_GENRES);
  const [sortBy, setSortBy]           = useState('title');
  const [showFilters,   setShowFilters]   = useState(false);
  const [omdbResults,   setOmdbResults]   = useState([]);
  const [omdbLoading,   setOmdbLoading]   = useState(false);
  const [omdbSearched,  setOmdbSearched]  = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true);
      const data = await getFilms();
      setFilms(data);
      const statsMap = {};
      await Promise.all(data.map(async film => {
        const ratings = await getFilmRatings(film.id);
        const avg = ratings.length
          ? ratings.reduce((s, x) => s + x.rating, 0) / ratings.length
          : null;
        statsMap[film.id] = { avg, count: ratings.length };
      }));
      setFilmStats(statsMap);
      setLoading(false);
    })();
  }, []);

  // OMDB search — fires 600ms after user stops typing
  useEffect(() => {
    if (search.length < 3) { setOmdbResults([]); setOmdbSearched(''); return; }
    const timer = setTimeout(async () => {
      setOmdbLoading(true);
      const results = await searchOMDB(search);
      // Filter out films already in our database (by title match)
      const localTitles = new Set(films.map(f => f.title.toLowerCase()));
      const filtered = results.filter(r => !localTitles.has(r.Title.toLowerCase()));
      setOmdbResults(filtered.slice(0, 6));
      setOmdbSearched(search);
      setOmdbLoading(false);
    }, 600);
    return () => clearTimeout(timer);
  }, [search, films]);

  const allLangs  = useMemo(() => [...new Set(films.map(f => f.language))].sort(), [films]);
  const allGenres = useMemo(() => [...new Set(films.flatMap(f => f.genres || []))].sort(), [films]);

  const filtered = useMemo(() => films
    .filter(f => {
      const q = search.toLowerCase();
      return (
        (f.title.toLowerCase().includes(q) || f.language?.toLowerCase().includes(q)) &&
        (filterType  === ALL_TYPES  || (f.type || 'movie') === filterType) &&
        (filterLang  === ALL_LANGS  || f.language === filterLang) &&
        (filterGenre === ALL_GENRES || (f.genres || []).includes(filterGenre))
      );
    })
    .sort((a, b) => {
      if (sortBy === 'title')  return a.title.localeCompare(b.title);
      if (sortBy === 'year')   return (b.year || 0) - (a.year || 0);
      if (sortBy === 'rating') return (filmStats[b.id]?.avg || 0) - (filmStats[a.id]?.avg || 0);
      return 0;
    }),
  [films, search, filterType, filterLang, filterGenre, sortBy, filmStats]);

  const activeFilters = (filterLang !== ALL_LANGS ? 1 : 0) + (filterGenre !== ALL_GENRES ? 1 : 0) + (filterType !== ALL_TYPES ? 1 : 0);

  const sel = {
    background: 'var(--surface2)', border: '1px solid var(--border2)',
    color: 'var(--text-dim)', borderRadius: 7, padding: '9px 12px',
    fontSize: 14, fontFamily: "'Crimson Text', serif", cursor: 'pointer',
    outline: 'none',
  };

  return (
    <>
      <style>{`
        .hp-filters-row { display: flex; }
        .hp-filters-panel { display: none; }
        .hp-filter-toggle { display: none; }
        @media (max-width: 640px) {
          .hp-filters-row { display: none !important; }
          .hp-filters-panel { display: flex !important; flex-direction: column; gap: 8px; }
          .hp-filter-toggle { display: flex !important; }
          .film-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 10px !important; }
        }
        @media (max-width: 380px) {
          .film-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
      `}</style>

      <div style={{ minHeight: '100vh' }}>
        <Navbar
          onAuthOpen={onAuthOpen}
          onAdminClick={onAdminClick}
          onChatClick={onChatClick}
          onBattleClick={onBattleClick}
          onRankingsClick={onRankingsClick}
          onTasteClick={onTasteClick}
          onRequestFilm={() => setShowRequest(true)}
        />

        {/* ── HERO ── */}
        <section style={{
          background: 'linear-gradient(180deg, #07070f 0%, var(--black) 100%)',
          padding: 'clamp(28px,5vw,56px) 16px 28px',
          textAlign: 'center', position: 'relative', overflow: 'hidden',
          borderBottom: '1px solid var(--border)',
        }}>
          {/* Glow blob */}
          <div style={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%,-50%)',
            width: 'min(640px,90%)', height: 280, borderRadius: '50%',
            background: 'radial-gradient(ellipse, rgba(212,175,55,0.07) 0%, transparent 70%)',
            pointerEvents: 'none',
          }} />

          <div className="fade-up">
            <p style={{
              fontFamily: "'DM Mono', monospace", fontSize: 10,
              color: 'var(--gold-dim)', letterSpacing: 4,
              textTransform: 'uppercase', marginBottom: 14,
            }}>The world watches. The world speaks.</p>

            <h1 style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: 'clamp(26px, 6vw, 58px)',
              fontWeight: 900, color: 'var(--text)',
              margin: '0 0 10px', lineHeight: 1.12,
            }}>
              Every Film.<br />
              <span style={{ color: 'var(--gold)', fontStyle: 'italic' }}>Every Review.</span>
            </h1>

            <p style={{ color: 'var(--text-dim)', fontSize: 'clamp(13px,2.5vw,17px)', maxWidth: 480, margin: '0 auto 26px' }}>
              {films.filter(f => (f.type||'movie')==='movie').length}+ curated films · {allLangs.length} languages · millions more via search
            </p>
          </div>


          {/* Movie / Series tabs */}
          <div className="fade-up-1" style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 16 }}>
            {[['All', '🎬 All'], ['movie', '🎥 Movies'], ['series', '📺 Series']].map(([val, label]) => (
              <button key={val} onClick={() => { setFilterType(val); setFilterLang(ALL_LANGS); setFilterGenre(ALL_GENRES); setSearch(''); }} style={{
                padding: '8px 18px', borderRadius: 20, border: 'none', cursor: 'pointer',
                fontFamily: "'Crimson Text', serif", fontSize: 14, transition: 'all 0.18s',
                background: filterType === val ? 'var(--gold)' : 'var(--surface2)',
                color: filterType === val ? 'var(--black)' : 'var(--text-dim)',
                fontWeight: filterType === val ? 700 : 400,
                boxShadow: filterType === val ? '0 2px 12px rgba(212,175,55,0.3)' : 'none',
              }}>{label}</button>
            ))}
          </div>

          {/* Search */}
          <div className="fade-up-1" style={{ maxWidth: 540, margin: '0 auto 14px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', fontSize: 15, pointerEvents: 'none' }}>🔍</span>
            <input
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search films or languages…"
              style={{
                width: '100%', background: 'var(--surface)',
                border: '1px solid var(--border2)', borderRadius: 10,
                color: 'var(--text)', padding: '11px 16px 11px 42px',
                fontSize: 15, outline: 'none', transition: 'border-color 0.2s',
                fontFamily: "'Crimson Text', serif",
              }}
              onFocus={e => e.target.style.borderColor = 'var(--gold-dim)'}
              onBlur={e => e.target.style.borderColor = 'var(--border2)'}
            />
          </div>

          {/* Desktop filter row */}
          <div className="fade-up-2 hp-filters-row" style={{ flexWrap: 'wrap', gap: 8, justifyContent: 'center', alignItems: 'center' }}>
            <select value={filterLang}  onChange={e => setFilterLang(e.target.value)}  style={sel}>
              <option>{ALL_LANGS}</option>
              {allLangs.map(l => <option key={l}>{l}</option>)}
            </select>
            <select value={filterGenre} onChange={e => setFilterGenre(e.target.value)} style={sel}>
              <option>{ALL_GENRES}</option>
              {allGenres.map(g => <option key={g}>{g}</option>)}
            </select>
            <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={sel}>
              <option value="title">A → Z</option>
              <option value="year">Newest</option>
              <option value="rating">Top Rated</option>
            </select>
            {activeFilters > 0 && (
              <button onClick={() => { setFilterLang(ALL_LANGS); setFilterGenre(ALL_GENRES); setFilterType(ALL_TYPES); }}
                style={{ background: 'none', border: '1px solid var(--border2)', color: 'var(--text-muted)', borderRadius: 6, padding: '8px 12px', fontSize: 13, cursor: 'pointer', fontFamily: "'Crimson Text', serif" }}>
                ✕ Clear
              </button>
            )}
            <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: 'var(--text-muted)' }}>
              {filtered.length} film{filtered.length !== 1 ? 's' : ''}
            </span>
          </div>

          {/* Mobile filter toggle + panel */}
          <div className="fade-up-2" style={{ maxWidth: 440, margin: '0 auto' }}>
            <div className="hp-filter-toggle" style={{ gap: 8, justifyContent: 'center', marginBottom: showFilters ? 10 : 0 }}>
              <button
                onClick={() => setShowFilters(p => !p)}
                style={{
                  background: activeFilters > 0 ? 'rgba(212,175,55,0.1)' : 'var(--surface2)',
                  border: `1px solid ${activeFilters > 0 ? 'var(--gold-dim)' : 'var(--border2)'}`,
                  color: activeFilters > 0 ? 'var(--gold)' : 'var(--text-dim)',
                  borderRadius: 7, padding: '8px 14px', fontSize: 13,
                  cursor: 'pointer', fontFamily: "'Crimson Text', serif",
                }}>
                ⚙ {showFilters ? 'Hide Filters' : `Filters${activeFilters ? ` (${activeFilters})` : ''}`}
              </button>
              <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ ...sel, flex: 1 }}>
                <option value="title">A → Z</option>
                <option value="year">Newest</option>
                <option value="rating">Top Rated</option>
              </select>
            </div>

            {showFilters && (
              <div className="hp-filters-panel" style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px' }}>
                <select value={filterLang}  onChange={e => setFilterLang(e.target.value)}  style={{ ...sel, width: '100%' }}>
                  <option>{ALL_LANGS}</option>
                  {allLangs.map(l => <option key={l}>{l}</option>)}
                </select>
                <select value={filterGenre} onChange={e => setFilterGenre(e.target.value)} style={{ ...sel, width: '100%' }}>
                  <option>{ALL_GENRES}</option>
                  {allGenres.map(g => <option key={g}>{g}</option>)}
                </select>
                {activeFilters > 0 && (
                  <button onClick={() => { setFilterLang(ALL_LANGS); setFilterGenre(ALL_GENRES); setFilterType(ALL_TYPES); }}
                    style={{ width: '100%', background: 'none', border: '1px solid var(--border2)', color: 'var(--text-muted)', borderRadius: 6, padding: '8px', fontSize: 13, cursor: 'pointer', fontFamily: "'Crimson Text', serif" }}>
                    ✕ Clear Filters
                  </button>
                )}
              </div>
            )}
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
              {filtered.length} film{filtered.length !== 1 ? 's' : ''}
            </div>
          </div>
        </section>

        {/* ── GRID ── */}
        <main style={{ maxWidth: 1280, margin: '0 auto', padding: 'clamp(16px,3vw,32px) clamp(10px,2vw,20px) 60px' }}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><Spinner size={40} /></div>
          ) : filtered.length === 0 && omdbResults.length === 0 && !omdbLoading ? (
            <div style={{ textAlign: 'center', padding: '60px 16px' }}>
              <div style={{ fontSize: 44, marginBottom: 14 }}>🎬</div>
              <h3 style={{ fontFamily: "'Playfair Display', serif", color: 'var(--text-dim)', marginBottom: 8 }}>No films found</h3>
              <p style={{ color: 'var(--text-muted)', marginBottom: 20 }}>We might not have it yet.</p>
              <GoldBtn outline onClick={() => setShowRequest(true)}>Request this film</GoldBtn>
            </div>
          ) : (
            <>
              {/* Local films */}
              {filtered.length > 0 && (
                <>
                  {search && omdbResults.length > 0 && (
                    <div style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 14,
                      fontFamily: "'DM Mono', monospace", letterSpacing: 0.5 }}>
                      IN CRITIQR ({filtered.length})
                    </div>
                  )}
                  <div className="film-grid" style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(175px, 1fr))',
                    gap: 16,
                  }}>
                    {filtered.map((film, i) => (
                      <div key={film.id} className="fade-up" style={{ animationDelay: `${Math.min(i * 0.03, 0.4)}s` }}>
                        <FilmCard
                          film={film}
                          avgRating={filmStats[film.id]?.avg}
                          ratingCount={filmStats[film.id]?.count || 0}
                          reviewCount={filmStats[film.id]?.reviews || 0}
                          onClick={() => onFilmClick(film)}
                        />
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* OMDB results */}
              {search.length >= 3 && (
                <div style={{ marginTop: filtered.length > 0 ? 36 : 0 }}>
                  {omdbLoading ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--text-muted)', fontSize: 13, marginBottom: 16 }}>
                      <div style={{ width: 16, height: 16, border: '2px solid var(--border)', borderTopColor: 'var(--gold)',
                        borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                      Searching global database…
                    </div>
                  ) : omdbResults.length > 0 ? (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                        <div style={{ color: 'var(--text-muted)', fontSize: 13,
                          fontFamily: "'DM Mono', monospace", letterSpacing: 0.5 }}>
                          ALSO FOUND ({omdbResults.length})
                        </div>
                        <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                        <div style={{ fontSize: 10, color: 'var(--text-muted)', fontFamily: "'DM Mono', monospace" }}>
                          powered by OMDb
                        </div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(175px, 1fr))', gap: 16 }}>
                        {omdbResults.map(result => {
                          const hasPoster = result.Poster && result.Poster !== 'N/A';
                          return (
                            <div key={result.imdbID} onClick={() => onFilmClick({ ...omdbToFilm(result), _fromOMDB: true })}
                              style={{ background: 'var(--surface)', border: '1px solid var(--border)',
                                borderRadius: 12, overflow: 'hidden', cursor: 'pointer', transition: 'all 0.2s' }}
                              onMouseEnter={e => { e.currentTarget.style.borderColor='var(--gold-dim)'; e.currentTarget.style.transform='translateY(-4px)'; }}
                              onMouseLeave={e => { e.currentTarget.style.borderColor='var(--border)'; e.currentTarget.style.transform='translateY(0)'; }}
                            >
                              {/* Poster */}
                              <div style={{ height: 200, background: 'var(--surface2)', position: 'relative', overflow: 'hidden' }}>
                                {hasPoster ? (
                                  <img src={result.Poster} alt={result.Title}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top' }} />
                                ) : (
                                  <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center',
                                    justifyContent: 'center', fontSize: 48 }}>🎬</div>
                                )}
                                {/* OMDb badge */}
                                <div style={{ position: 'absolute', top: 8, right: 8,
                                  background: 'rgba(212,175,55,0.9)', borderRadius: 4,
                                  padding: '2px 6px', fontSize: 9, fontFamily: "'DM Mono', monospace",
                                  color: 'var(--black)', fontWeight: 700, letterSpacing: 0.5 }}>OMDb</div>
                                {/* Type badge for series */}
                                {result.Type === 'series' && (
                                  <div style={{ position: 'absolute', bottom: 8, left: 8,
                                    background: 'rgba(59,130,246,0.85)', borderRadius: 4,
                                    padding: '2px 7px', fontSize: 9, color: '#fff',
                                    fontFamily: "'DM Mono', monospace", letterSpacing: 1 }}>📺 SERIES</div>
                                )}
                              </div>
                              {/* Info */}
                              <div style={{ padding: '12px 14px' }}>
                                <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 14,
                                  fontWeight: 700, color: 'var(--text)', marginBottom: 2,
                                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {result.Title}
                                </div>
                                <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>{result.Year}</div>
                                <div style={{ color: 'var(--gold-dim)', fontSize: 11, marginTop: 6,
                                  fontFamily: "'DM Mono', monospace" }}>View on Critiqr →</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  ) : omdbSearched === search && filtered.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px 16px' }}>
                      <div style={{ fontSize: 40, marginBottom: 12 }}>🔍</div>
                      <div style={{ color: 'var(--text-muted)', fontSize: 15, marginBottom: 16 }}>No results found for "{search}"</div>
                      <GoldBtn outline onClick={() => setShowRequest(true)}>Request this film</GoldBtn>
                    </div>
                  ) : null}
                </div>
              )}
            </>
          )}
        </main>

        {/* Footer */}
        <footer style={{ borderTop: '1px solid var(--border)', padding: '18px 16px', textAlign: 'center' }}>
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: 'var(--text-muted)', letterSpacing: 1 }}>
            CRITIQR · WORLD CINEMA ·{' '}
            <button onClick={() => setShowRequest(true)} style={{ background: 'none', border: 'none', color: 'var(--gold-dim)', cursor: 'pointer', fontSize: 10, fontFamily: "'DM Mono', monospace", letterSpacing: 1 }}>
              REQUEST A FILM
            </button>
          </div>
        </footer>

        <RequestFilmModal open={showRequest} onClose={() => setShowRequest(false)} />
      </div>
    </>
  );
}
